import type { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import type {
    ICampaignLocationAndLanguage,
    ISupportedLanguagesAndLocale,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { getLanguagesList } from "app/components/campaigns/campaigns-wizard/campaignsWizardFetch";
import { getLocationsForCampaigns } from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import { isPerformanceAddOnEnabled } from "app/components/campaigns/shared/utils";
import useLocalStorage from "app/custom-hooks/useLocalStorage";
import {
    createListOfCampaignsWithIcons,
    fetchAddOnCampaignsMutualAssets,
    getBPCampaign,
    getBPCampaignsFromServer,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/infra/BrandMonitoringCampaignsInfraUtils";
import type {
    IBPCampaignDataFromServer,
    IBPCampaignDataFromServerAndAssets,
    IBrandMonitoringCampaignsContext,
    IBrandMonitoringCampaignsEmailConfigurations,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/BPCampaignsWizard.types";
import { EUpdateCampaignConfig } from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/BPCampaignsWizard.types";
import { enrichCampaignsWithIcons } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import {
    getIsRTCreateCampaignsAvailable,
    getMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type {
    ICampaignsConsumptionMetrics,
    IMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { ECampaignsLoadingState } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { IAssetsList } from "app/pages/digital-marketing/keywords-campaigns/keywords-campaign-homepage/KeywordsCampaignsHomePage";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import { fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";

import { BRAND_MONITORING_COMPETITORS_REPORT_STATE } from "../reports/competitors/BrandMonitoringCompetitorsReportConsts";

import { getUpdatedCampaignConfigurations, getUpdatedCampaignItem } from "./brandMonitoringCampaignsContextUtils";

export const BRAND_PROTECTION_CAMPAIGNS_CONFIGS_KEY = "BRAND_MONITORING_CAMPAIGNS_CONFIG";

const context = createContext<IBrandMonitoringCampaignsContext>(null);

export const useBrandMonitoringCampaignsContext = () => {
    return useContext(context);
};

interface IBrandMonitoringCampaignsProviderProps {
    children: ReactNode;
}
export const BrandMonitoringCampaignsProvider: FC<IBrandMonitoringCampaignsProviderProps> = ({ children }) => {
    const { params, state } = useCurrentStateAndParams();
    const { campaignId } = params;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<ECampaignsLoadingState>(ECampaignsLoadingState.LOADING);
    // campaigns are saved in local storage so that the report page sidebar can have access to
    // them, since the sidebar does not exist under the scope of the brand protection context provider
    const [, setCampaignsConfigurationsFromLocalStorage] = useLocalStorage(BRAND_PROTECTION_CAMPAIGNS_CONFIGS_KEY); //required for tacking (is_asset_owner)
    const [locationsAndLanguagesLists, setLocationsAndLanguagesLists] = useState<ICampaignLocationAndLanguage>({
        languagesList: [],
        locationsList: [],
    });
    const [myUserConsumptionMetrics, setMyUserConsumptionMetrics] = useState<IMyUserConsumptionMetrics>(null);
    const [durationObject, setDurationObject] = useState<DatesRange>(null);
    const [currentCampaignConfiguration, setCurrentCampaignConfiguration] = useState<IBPCampaignDataFromServer>(null);
    const [campaignsConfigurations, setCampaignsConfigurations] = useState<IBPCampaignDataFromServer[]>(null);
    const [faviconsList, setFaviconsList] = useState<IAssetsList>({});
    const [campaignsConsumptionMetrics, setCampaignsConsumptionMetrics] = useState<ICampaignsConsumptionMetrics>({
        usedKeywords: 0,
        totalKeywords: swSettings.components.BrandMonitor.resources.NumberTrackedKeywords,
        usedCampaigns: 0,
        totalCampaigns: swSettings.components.BrandMonitor.resources.NumberOfCampaigns,
        hasMutualAssetsError: false,
    });
    const getLanguagesFromServer = async (useDemoCampaignsApi: boolean) => {
        let languagesList: ISupportedLanguagesAndLocale[];
        try {
            languagesList = await getLanguagesList(useDemoCampaignsApi);
        } catch (error) {
            Sentry.error(`Error fetching languages - brand protection -`, error);
            languagesList = [{ description: "English", locale: "en-us" }];
        }
        setLocationsAndLanguagesLists((locationsAndLanguagesLists) => ({
            languagesList,
            locationsList: locationsAndLanguagesLists.locationsList,
        }));
    };

    const getLocationsFromServer = async (useDemoCampaignsApi: boolean) => {
        const locationsList = await getLocationsForCampaigns(useDemoCampaignsApi);
        setLocationsAndLanguagesLists((locationsAndLanguagesLists) => ({
            languagesList: locationsAndLanguagesLists.languagesList,
            locationsList,
        }));
    };

    const setCampaignsWithIcons = (listOfFavicons: IAssetsList, campaignConfigs: IBPCampaignDataFromServer[]) => {
        const ListOfCampaignsWithIcons = createListOfCampaignsWithIcons(listOfFavicons, campaignConfigs);
        setCampaignsConfigurations(ListOfCampaignsWithIcons.managedCampaigns);
        setCampaignsConfigurationsFromLocalStorage(ListOfCampaignsWithIcons.campaignsAsString);
    };

    const getCampaignsConsumptionFromServer = () => {
        fetchAddOnCampaignsMutualAssets().then(
            ({ CrossAccountExistingCampaigns, CrossAccountExistingKeywords }) => {
                setCampaignsConsumptionMetrics((state) => ({
                    ...state,
                    usedKeywords: CrossAccountExistingKeywords,
                    usedCampaigns: CrossAccountExistingCampaigns,
                    hasMutualAssetsError: false,
                }));
            },
            (errorFetch) => {
                setCampaignsConsumptionMetrics((state) => ({
                    ...state,
                    hasMutualAssetsError: true,
                }));
                Sentry.error(
                    `Error fetching limitations of mutual assets for amount of used campaigns and keywords from server -`,
                    errorFetch,
                );
            },
        );
    };

    const updateMyUserConsumptionMetrics = (campaignConfigs: IBPCampaignDataFromServer[]): void => {
        const consumptionResults = getMyUserConsumptionMetrics(campaignConfigs);
        setMyUserConsumptionMetrics(consumptionResults);
    };

    const updateCampaignsSubscriptions = (
        updatedCampaignId: string,
        emailConfigurations?: IBrandMonitoringCampaignsEmailConfigurations[],
    ) => {
        setCampaignsConfigurations((campaignsState) =>
            campaignsState.map((campaign) => {
                if (campaign.id === updatedCampaignId) {
                    return {
                        ...campaign,
                        emailConfigurations,
                    };
                }
                return campaign;
            }),
        );
    };

    const updateCampaignsFromServer = async () => {
        const campaignsAndAssets: IBPCampaignDataFromServerAndAssets = await getBPCampaignsFromServer();

        if (!campaignsAndAssets) {
            setCampaignsConsumptionMetrics((state) => ({
                ...state,
                hasMutualAssetsError: true,
            }));
            setIsError(ECampaignsLoadingState.ERROR);
            setIsLoading(false);
        } else {
            updateMyUserConsumptionMetrics(campaignsAndAssets.campaignConfigs);
            setFaviconsList(campaignsAndAssets.assets);
            setCampaignsWithIcons(campaignsAndAssets.assets, campaignsAndAssets.campaignConfigs);
        }
    };

    useEffect(() => {
        const isHomePage = state.name?.includes("home");
        if (state.name !== BRAND_MONITORING_COMPETITORS_REPORT_STATE && isPerformanceAddOnEnabled()) {
            getCampaignsConsumptionFromServer();
        }
        if (!campaignsConfigurations || isHomePage) {
            updateCampaignsFromServer();
            setCurrentCampaignConfiguration(null);
        }
        if (!locationsAndLanguagesLists?.languagesList?.length || !locationsAndLanguagesLists?.locationsList?.length) {
            const useDemoCampaignsApi = !getIsRTCreateCampaignsAvailable();
            getLanguagesFromServer(useDemoCampaignsApi);
            getLocationsFromServer(useDemoCampaignsApi);
        }
    }, [state.name]);

    const getAvailableDurationObjectForBPContext = <T extends { createdTime?: Dayjs }>(
        currentCampaignConfig: T,
        lastEtlDate: string,
    ): DatesRange => {
        const createdTime = currentCampaignConfig?.createdTime;
        const maxCampaignDate: Dayjs = lastEtlDate ? dayjs.utc(lastEtlDate) : dayjs().utc();
        const minCampaignDate: Dayjs = createdTime?.isBefore(maxCampaignDate) ? createdTime : maxCampaignDate;
        const durationObject = { from: minCampaignDate, to: maxCampaignDate };
        return durationObject;
    };

    useEffect(() => {
        const isEditSettingsPage = state.name.includes("settings"); //as we might update keywords in the reports we need to get the most updated data
        if (campaignId && (isEditSettingsPage || currentCampaignConfiguration?.id !== campaignId)) {
            (async () => {
                // If campaignsConfigurations is null (first-time user), we need to fetch it first
                let campaignsConfigsToUse = campaignsConfigurations;
                if (!campaignsConfigsToUse) {
                    const campaignsAndAssets = await getBPCampaignsFromServer();
                    if (campaignsAndAssets) {
                        setCampaignsWithIcons(campaignsAndAssets.assets, campaignsAndAssets.campaignConfigs);
                        campaignsConfigsToUse = campaignsAndAssets.campaignConfigs;
                    }
                }

                const currentCampaign = await getBPCampaign(campaignId, campaignsConfigsToUse);
                const campaignWithIcons = await enrichCampaignsWithIcons([currentCampaign]);
                const lastEtlDate = swSettings.components.BrandMonitor?.resources?.LastAvailableBrandMonitorEtlDate;
                const durationObject = getAvailableDurationObjectForBPContext(currentCampaign, lastEtlDate);
                setDurationObject(durationObject);
                setCurrentCampaignConfiguration(campaignWithIcons[0] as any); //todo: chanmge to IBPCurrentCampaignData
            })();
        } else if (!campaignId) {
            setCurrentCampaignConfiguration(null);
        }
    }, [campaignId, state.name]);

    useEffect(() => {
        if (campaignsConfigurations && locationsAndLanguagesLists?.languagesList.length !== 0) {
            setIsLoading(false);
        }
    }, [campaignsConfigurations, locationsAndLanguagesLists?.languagesList]);

    const updateCampaignConfigurations = async (
        operationType: EUpdateCampaignConfig,
        newConfiguration?: IBPCampaignDataFromServer,
        configurationId?: string,
    ) => {
        let updatedCampaignConfigurations;
        if (operationType === EUpdateCampaignConfig.UPDATE) {
            const indexOfItemToReplace = campaignsConfigurations?.findIndex(
                (config) => config.id === newConfiguration?.id,
            );

            const updatedItem = getUpdatedCampaignItem(indexOfItemToReplace, campaignsConfigurations, newConfiguration);

            updatedCampaignConfigurations = getUpdatedCampaignConfigurations(
                indexOfItemToReplace,
                campaignsConfigurations,
                updatedItem,
            );
        } else if (operationType === EUpdateCampaignConfig.CREATE) {
            let faviconForNewConfig;
            if (!faviconsList?.[newConfiguration?.site]) {
                faviconForNewConfig = await fetchWebsitesFaviconsQuery([newConfiguration?.site]);
                setFaviconsList((currentFaviconsList) => ({ ...currentFaviconsList, ...faviconForNewConfig }));
            } else {
                faviconForNewConfig = faviconsList[newConfiguration?.site];
            }
            const newItemToAdd = {
                ...newConfiguration,
                asset: faviconForNewConfig[newConfiguration?.site],
                createdTime: dayjs.utc(newConfiguration?.createdTime),
            };

            updatedCampaignConfigurations = campaignsConfigurations
                ? [...campaignsConfigurations, newItemToAdd]
                : [newItemToAdd];
        } else if (operationType === EUpdateCampaignConfig.DELETE) {
            updatedCampaignConfigurations = campaignsConfigurations.filter((config) => config.id !== configurationId);
        }
        setCampaignsConfigurations(updatedCampaignConfigurations);
        setCampaignsConfigurationsFromLocalStorage(JSON.stringify(updatedCampaignConfigurations));
    };

    const contextValue: IBrandMonitoringCampaignsContext = {
        isLoading,
        isError,
        campaignsConfigurations,
        currentCampaignConfiguration,
        updateCampaignsFromServer,
        languagesList: locationsAndLanguagesLists.languagesList,
        locationsAndLanguagesLists,
        durationObject,
        updateCampaignConfigurations,
        campaignsConsumptionMetrics,
        myUserConsumptionMetrics,
        updateCampaignsSubscriptions,
    };

    return <context.Provider value={contextValue}>{children}</context.Provider>;
};
